import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '@mantine/core';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { FileInputControl, Form } from '@/core/components';

import { useAppNotification } from '@feature/utils';

import { useUserDocumentAdminUpload } from '../model/UserDocumentQuery';
import { UserDocumentKind, userDocumentAccept } from '../model/UserDocumentTypes';

interface UserDocumentAdminUploadModalProps {
  userId: number;
  kind: UserDocumentKind;
  onClose: () => void;
}

const schema = z.object({
  file: z.custom<File>(value => value instanceof File, { params: { i18n: 'custom.fileRequired' } }),
});

type UserDocumentAdminUploadSchema = z.infer<typeof schema>;

export function UserDocumentAdminUploadModal({ userId, kind, onClose }: UserDocumentAdminUploadModalProps) {
  const { t } = useTranslation();
  const notification = useAppNotification();
  const { mutate, isPending } = useUserDocumentAdminUpload();

  const form = useForm<UserDocumentAdminUploadSchema>({
    resolver: zodResolver(schema),
  });

  const handleSubmit = (values: UserDocumentAdminUploadSchema) => {
    mutate(
      { userId, kind, file: values.file },
      {
        onError: notification.error,
        onSuccess: () => {
          notification.success();
          onClose();
        },
      },
    );
  };

  return (
    <Modal
      opened
      centered
      onClose={onClose}
      title={t(`userDocuments.kindEnum.${kind}`)}
      size="md"
    >
      <Form
        onSubmit={form.handleSubmit(handleSubmit)}
        onCancel={onClose}
        isSubmitting={isPending}
      >
        <FileInputControl
          name="file"
          label={t('userDocuments.file')}
          accept={userDocumentAccept}
          isSubmitting={isPending}
          withAsterisk
          control={form.control}
        />
      </Form>
    </Modal>
  );
}
